import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import Select from "react-select";
import { trackingSystemBreakRules } from "../../data/ApiEndpoints";

const BreakRulesForm = ({
  refetchBreakRules,
  onCloseModal,
  modValues,
  setModValues,
  token,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    duration: "",
    description: "",
    // shift_type: { value: "single_shift", label: "Single Shift" }, // Commented out for now
  });

  // Populate form when editing
  useEffect(() => {
    // Shift type options for react-select - Commented out for now
    // const shiftTypeOptions = [
    //   { value: "single_shift", label: "Single Shift" },
    //   { value: "trainer", label: "Trainer" },
    //   { value: "double_shift", label: "Double Shift" },
    // ];

    if (modValues.cardName === "edit" && modValues.rowData) {
      const { rowData } = modValues;
      // const shiftTypeOption =
      //   shiftTypeOptions.find(
      //     (option) => option.value === rowData.shift_type
      //   ) || shiftTypeOptions[0];

      setFormData({
        name: rowData.name || "",
        duration: rowData.duration || "",
        description: rowData.description || "",
        // shift_type: shiftTypeOption, // Commented out for now
      });
    } else {
      // Reset form for create
      setFormData({
        name: "",
        duration: "",
        description: "",
        // shift_type: shiftTypeOptions[0], // Commented out for now
      });
    }
  }, [modValues]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enhanced validation
    if (!formData.name?.trim()) {
      toast.error("Break name is required");
      return;
    }

    if (!formData.duration || parseInt(formData.duration) <= 0) {
      toast.error("Duration must be greater than 0 minutes");
      return;
    }

    if (parseInt(formData.duration) > 480) {
      toast.error("Duration cannot exceed 1 hours (60 minutes)");
      return;
    }

    // if (!formData.shift_type?.value) {
    //   toast.error("Please select a shift type");
    //   return;
    // }

    setLoading(true);

    try {
      const isEditing = modValues.cardName === "edit";
      const endpoint = isEditing
        ? `${trackingSystemBreakRules}/${modValues.rowData.uid}`
        : trackingSystemBreakRules;
      const method = isEditing ? "PATCH" : "POST";

      const payload = {
        name: formData.name.trim(),
        duration: parseInt(formData.duration),
        description: formData.description?.trim() || "",
        // shift_type: formData.shift_type.value, // Commented out for now
      };

      const response = await dispatch(
        fetchApiData({
          endpoint,
          method,
          token,
          payload,
        })
      ).unwrap();

      if (response && response?.status === true) {
        toast.success(response?.message || "Break rule saved successfully");
        refetchBreakRules();
        onCloseModal();
      } else {
        toast.error(response?.message || "Failed to save break rule");
      }
    } catch (error) {
      console.error("Error saving break rule:", error);
      toast.error(
        error?.message ||
          `Failed to ${
            modValues.cardName === "edit" ? "update" : "create"
          } break rule`
      );
    } finally {
      setLoading(false);
    }
  };

  const isEditing = modValues.cardName === "edit";

  // Shift type options for react-select (for JSX usage) - Commented out for now
  // const shiftTypeOptions = [
  //   { value: "single_shift", label: "Single Shift" },
  //   { value: "trainer", label: "Trainer" },
  //   { value: "double_shift", label: "Double Shift" },
  // ];

  return (
    <div className="break-form-container enterprise-form">
      <div style={{ padding: "20px" }}>
        <ModalHeadAndClose
          title={`${isEditing ? "Update" : "Create"} Break Rule`}
          subtitle={
            isEditing
              ? "Modify the break rule settings below"
              : "Configure a new break rule for your team"
          }
          onClose={onCloseModal}
        />
      </div>

      <div className="break-form-content">
        {/* Summary Section for Editing - Commented out for now */}
        {/* {isEditing && formData.name && (
          <div className="current-rule-section">
            <h6 className="section-title">Current Break Rule</h6>
            <div className="rule-summary">
              <div className="rule-info">
                <span className="rule-name">{formData.name}</span>
                <span className="rule-details">
                  {formData.duration} minutes
                </span>
              </div>
            </div>
          </div>
        )} */}

        <Form onSubmit={handleSubmit}>
          {/* Break Details Section */}
          <div className="form-section">
            <h6 className="section-title">Break Details</h6>

            <Row className="form-row">
              <Col md={6}>
                <Form.Group className="form-group-enhanced">
                  <Form.Label className="form-label-enhanced">
                    Break Name <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Tea Break, Lunch Break"
                    className="form-control-enhanced"
                    maxLength={50}
                  />
                  <Form.Text className="form-hint">
                    Choose a clear, descriptive name for this break
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="form-group-enhanced">
                  <Form.Label className="form-label-enhanced">
                    Duration <span className="required">*</span>
                  </Form.Label>
                  <div className="duration-input-wrapper">
                    <Form.Control
                      type="number"
                      value={formData.duration}
                      onChange={(e) =>
                        handleInputChange("duration", e.target.value)
                      }
                      placeholder="15"
                      className="form-control-enhanced duration-input"
                      min="1"
                      max="60"
                    />
                    <span className="duration-unit">minutes</span>
                  </div>
                  <Form.Text className="form-hint">
                    Maximum 1 hours (60 minutes)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Shift Configuration Section - Commented out for now */}
          {/* <div className="form-section">
            <h6 className="section-title">Shift Configuration</h6>

            <Row className="form-row">
              <Col md={12}>
                <Form.Group className="form-group-enhanced">
                  <Form.Label className="form-label-enhanced">
                    Applicable Shift Type <span className="required">*</span>
                  </Form.Label>
                  <div className="select-wrapper">
                    <Select
                      value={formData.shift_type}
                      onChange={(selected) =>
                        handleInputChange("shift_type", selected)
                      }
                      options={shiftTypeOptions}
                      placeholder="Select shift type"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable={false}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base, state) => ({
                          ...base,
                          minHeight: "42px",
                          border: state.isFocused
                            ? "2px solid #007bff"
                            : "1px solid #ced4da",
                          borderRadius: "6px",
                          fontSize: "14px",
                          boxShadow: state.isFocused ? "none" : base.boxShadow,
                          "&:hover": {
                            border: state.isFocused
                              ? "2px solid #007bff"
                              : "1px solid #adb5bd",
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: "10px 12px",
                          fontSize: "14px",
                          backgroundColor: state.isSelected
                            ? "#007bff"
                            : state.isFocused
                            ? "#f8f9fa"
                            : "white",
                        }),
                      }}
                    />
                  </div>
                  <Form.Text className="form-hint">
                    This break rule will apply to the selected shift type
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div> */}

          {/* Additional Information Section */}
          <div className="form-section">
            <h6 className="section-title">Additional Information</h6>

            <Row className="form-row">
              <Col md={12}>
                <Form.Group className="form-group-enhanced">
                  <Form.Label className="form-label-enhanced">
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Provide a brief description of this break rule..."
                    className="form-control-enhanced"
                    maxLength={200}
                  />
                  <Form.Text className="form-hint">
                    Optional: Add context or special instructions for this break
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <div className="action-buttons">
              <Button
                variant="secondary"
                onClick={onCloseModal}
                disabled={loading}
                className="btn-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="btn-submit"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  `${isEditing ? "Update" : "Create"} Break Rule`
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default BreakRulesForm;
